import { useCallback, useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  MarkerF,
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";
import { CFormInput, CFormSelect, CButton } from "@coreui/react";
import { colors } from "../config/theme";
import { Cities } from "../helpers/constants";
import { Maximize, Minimize } from "lucide-react";

const LIBRARIES: ("places" | "drawing")[] = ["places", "drawing"];
const DEFAULT_CENTER = { lat: 33.5731, lng: -7.5898 };

export interface PickedLocation {
  latitude: number;
  longitude: number;
  address?: string;
  placeId?: string;
  name?: string;
}

interface LocationPickerProps {
  value?: PickedLocation;
  onChange: (location: PickedLocation) => void;
}

export default function LocationPicker({
  value,
  onChange,
}: LocationPickerProps) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_MAP_KEY as string}`,
    libraries: LIBRARIES,
  });

  const [searchText, setSearchText] = useState(value?.address || "");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapWrapperRef = useRef<HTMLDivElement | null>(null);
  const [cityFilter, setCityFilter] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Keep state in sync with the browser's fullscreen status
  // (covers the Esc key, F11, etc., not just our button)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === mapWrapperRef.current);
      // Google Maps needs a nudge to redraw/resize correctly
      if (mapRef.current) {
        google.maps.event.trigger(mapRef.current, "resize");
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Escape" &&
        document.fullscreenElement === mapWrapperRef.current
      ) {
        e.stopPropagation();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!mapWrapperRef.current) return;

    if (!document.fullscreenElement) {
      mapWrapperRef.current.requestFullscreen?.().catch(() => {
        // Fullscreen request can be rejected (e.g. no user gesture, unsupported) — ignore silently
      });
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  const onAutocompleteLoad = (ac: google.maps.places.Autocomplete) => {
    autocompleteRef.current = ac;
    ac.setComponentRestrictions({ country: "ma" }); // restrict to Morocco
  };

  const handleCityFilterChange = (cityLabel: string) => {
    setCityFilter(cityLabel);
    const city = Cities.find((c) => c.label === cityLabel);
    if (!city) return;

    const lat = Number(city.latitude);
    const lng = Number(city.longitude);

    // Bias search results toward that city (~20km radius)
    if (autocompleteRef.current) {
      const circle = new google.maps.Circle({
        center: { lat, lng },
        radius: 20000,
      });
      autocompleteRef.current.setBounds(
        circle.getBounds() as google.maps.LatLngBounds,
      );
    }

    // Zoom/pan the visible map to that city
    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(12);
  };

  const center = value
    ? { lat: value.latitude, lng: value.longitude }
    : DEFAULT_CENTER;

  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place || !place.geometry?.location) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    const location: PickedLocation = {
      latitude: lat,
      longitude: lng,
      address: place.formatted_address || place.name,
      placeId: place.place_id,
      name: place.name,
    };

    setSearchText(location.address || "");
    onChange(location);

    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(16);
  };

  const reverseGeocode = useCallback(
    (lat: number, lng: number) => {
      if (!geocoderRef.current) {
        geocoderRef.current = new google.maps.Geocoder();
      }
      geocoderRef.current.geocode(
        { location: { lat, lng } },
        (results, status) => {
          const address =
            status === "OK" && results?.[0]
              ? results[0].formatted_address
              : undefined;

          setSearchText(address || "");
          onChange({
            latitude: lat,
            longitude: lng,
            address,
            placeId: results?.[0]?.place_id,
          });
        },
      );
    },
    [onChange],
  );

  const onMapClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    reverseGeocode(e.latLng.lat(), e.latLng.lng());
  };

  const onMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    reverseGeocode(e.latLng.lat(), e.latLng.lng());
  };

  if (!isLoaded) {
    return <div className="text-muted small">Chargement de la carte...</div>;
  }

  return (
    <div>
      <CFormSelect
        className="mb-2"
        value={cityFilter}
        onChange={(e) => handleCityFilterChange(e.target.value)}
      >
        <option value="">Toutes les villes</option>
        {Cities.map((c) => (
          <option key={c.label} value={c.label}>
            {c.label}
          </option>
        ))}
      </CFormSelect>

      <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
        <CFormInput
          className="mb-2"
          placeholder="Rechercher un lieu (ex: Hôtel, Plage, Restaurant...)"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Autocomplete>

      <div
        className="small text-muted mb-2"
        style={{ display: "flex", alignItems: "center", gap: 6 }}
      >
        <span>Ou cliquez directement sur la carte pour placer un point</span>
      </div>

      <div
        ref={mapWrapperRef}
        style={{
          position: "relative",
          width: "100%",
          height: isFullscreen ? "100vh" : "280px",
        }}
      >
        <CButton
          size="sm"
          color="light"
          onClick={toggleFullscreen}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 5,
            boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
          }}
          title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
        >
          {isFullscreen ? <Minimize /> : <Maximize />}
        </CButton>

        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "100%",
            borderRadius: isFullscreen ? 0 : 10,
          }}
          center={center}
          zoom={value ? 15 : 12}
          onLoad={onMapLoad}
          onClick={onMapClick}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {value && (
            <MarkerF
              position={{ lat: value.latitude, lng: value.longitude }}
              draggable
              onDragEnd={onMarkerDragEnd}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: colors.yellow,
                fillOpacity: 1,
                strokeColor: "#fff",
                strokeWeight: 2,
              }}
            />
          )}
        </GoogleMap>
      </div>

      {value && (
        <div className="small text-muted mt-2">
          Lat: {value.latitude.toFixed(6)}, Lng: {value.longitude.toFixed(6)}
        </div>
      )}
    </div>
  );
}
